import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Filter, useFilter } from "@/partner/shared/components/Filter";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  SubscriptionStatusType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";
import { buildSubscriptionsFilter } from "../model/buildSubscriptionsFilter";

// Статусы подписки: подписи уже есть в сиде (`subscriptions.status.*`),
// значение опции — само enum-значение схемы, оно и уходит в `filter.status`.
const STATUS_OPTIONS: { value: SubscriptionStatusType; labelKey: string }[] = [
  {
    value: SubscriptionStatusType.ActiveType,
    labelKey: "subscriptions.status.active",
  },
  {
    value: SubscriptionStatusType.PastDueType,
    labelKey: "subscriptions.status.pastDue",
  },
  {
    value: SubscriptionStatusType.CanceledType,
    labelKey: "subscriptions.status.canceled",
  },
  {
    value: SubscriptionStatusType.ExpiredType,
    labelKey: "subscriptions.status.expired",
  },
];

// Автопродление: три состояния (любое / включено / выключено). В querystring
// значение живёт строкой, а FilterProvider.cleanFilterValues приводит
// "true"/"false" к булевым, поэтому в `filter` уже лежит boolean, а вот
// записывать надо строкой: changeParams удаляет ключ на любом falsy-значении
// (то есть boolean false никогда бы не сохранился). Подписи те же, что у
// чипа автопродления в списке.
const RENEW_OPTIONS: { value: "true" | "false"; labelKey: string }[] = [
  { value: "true", labelKey: "subscriptions.list.renewOn" },
  { value: "false", labelKey: "subscriptions.list.renewOff" },
];

export const SubscriptionsFilter: FC = () => {
  const { t } = useTranslation();
  const { filter } = useFilter();

  return (
    <Filter
      title={t("subscriptions.pageTitle")}
      action={
        <DownloadReportButton
          type={ReportTypeType.SubscriptionsType}
          filter={
            buildSubscriptionsFilter(filter) as
              | Record<string, unknown>
              | undefined
          }
        />
      }
      selects={[
        {
          name: "status",
          label: t("subscriptions.filter.statusLabel"),
          options: STATUS_OPTIONS.map(({ value, labelKey }) => ({
            value,
            label: t(labelKey),
          })),
        },
        {
          name: "renewSubscription",
          label: t("subscriptions.filter.renewLabel"),
          options: RENEW_OPTIONS.map(({ value, labelKey }) => ({
            value,
            label: t(labelKey),
          })),
        },
      ]}
      fields={{
        search: true,
        dateFrom: true,
        dateTo: true,
        productIds: true,
      }}
    />
  );
};
